import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Banner, Button, Checkbox, Dialog, Skeleton, TextField } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { setDnc, useDncPreview } from './api';
import type { DncPreview } from './types';
import styles from './dialogs.module.css';

export interface DncDialogProps {
  open: boolean;
  contact: { id: string; name: string };
  onClose: () => void;
  onDone: (result: DncPreview & { gdprApprovalId: string | null }) => void;
}

function tasksText(n: number): string {
  return n === 1 ? '1 wird geschlossen' : `${n} werden geschlossen`;
}

function listsText(n: number): string {
  if (n === 0) return 'in keiner Dialer-Liste';
  return n === 1 ? 'aus 1 Dialer-Liste entfernt' : `aus ${n} Dialer-Listen entfernt`;
}

/** Kontakte 07 · „DNC setzen": the consequences from the preview, the mandatory reason, the GDPR option. */
export function DncDialog({ open, contact, onClose, onDone }: DncDialogProps) {
  const preview = useDncPreview(contact.id, open);
  const [reason, setReason] = useState('');
  const [gdpr, setGdpr] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setGdpr(false);
    setError(null);
    setBanner(null);
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (reason.trim() === '') {
      setError('Bitte geben Sie einen Grund an.');
      return;
    }
    setError(null);
    setBanner(null);
    setPending(true);
    try {
      const result = await setDnc(contact.id, { reason: reason.trim(), requestGdprDeletion: gdpr });
      onDone(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && err.fields['reason'])
        setError(err.fields['reason']);
      else setBanner(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  const formId = 'dnc-dialog-form';
  const rows: { label: string; value: ReactNode }[] = [
    { label: 'Anrufe', value: 'gesperrt, auch im Dialer' },
    { label: 'E-Mail, WhatsApp', value: 'gesperrt, geplante Nachrichten werden verworfen' },
    {
      label: 'Offene Tasks',
      value: preview.data ? tasksText(preview.data.openTasks) : <Skeleton width={120} />,
    },
    {
      label: 'Listen',
      value: preview.data ? listsText(preview.data.dialerLists) : <Skeleton width={140} />,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Kontaktsperre"
      title={`${contact.name} nicht mehr kontaktieren?`}
      subtitle="DNC ist dauerhaft und gilt unabhängig vom Lifecycle."
      width={560}
      footerNote="Aufheben nur durch Admin"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} variant="destructiveFilled" loading={pending}>
            DNC setzen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {preview.isError ? <Banner tone="danger">{errorMessage(preview.error)}</Banner> : null}
        <div className={styles.effects}>
          {rows.map((row) => (
            <div key={row.label} className={styles.effectRow}>
              <span className={styles.effectLabel}>{row.label}</span>
              <span className={styles.effectValue}>{row.value}</span>
            </div>
          ))}
        </div>
        <TextField
          label="Grund"
          required
          value={reason}
          error={error ?? undefined}
          onChange={(event) => setReason(event.target.value)}
        />
        <Checkbox
          label="Zusätzlich DSGVO-Löschung beantragt"
          description="Sperrt den Kontakt und legt eine Admin-Task zur Prüfung an"
          checked={gdpr}
          onChange={(event) => setGdpr(event.target.checked)}
        />
      </form>
    </Dialog>
  );
}
